import React from "react";
import { ActivityIndicator, Platform, Text, TextInput, TouchableOpacity, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { v2Styles as styles } from "./LocationSummaryV2.styles";

interface Props {
  query: string;
  isSearchFocused: boolean;
  isMobileToolbar: boolean;
  columnsAnchorRef: React.MutableRefObject<View | null>;
  onSearchChange: (value: string) => void;
  onSearchFocus: () => void;
  onSearchBlur: () => void;
  onToggleColumns: () => void;
  onExport: () => void;
  /** True while an export is in flight — disables the button and shows a
   * spinner so duplicate requests cannot be fired. */
  isExporting?: boolean;
  /** True when the table has no rows to export — disables the button. */
  disableExport?: boolean;
  /** True while an API call is in flight — disables search, Columns, and Export so no duplicate requests can be fired. */
  disabled?: boolean;
  columnsDropdown: React.ReactNode;
}

// Inline style applied directly to the TextInput element on web so that it
// cannot be overridden by any class-level :focus / :focus-visible rule
// (inline styles always win over stylesheet classes in CSS specificity).
const webInputFocusReset =
  Platform.OS === "web"
    ? ({
        outlineStyle: "none",
        outlineWidth: 0,
        outlineColor: "transparent",
        boxShadow: "none",
      } as any)
    : {};

const LocationSummaryToolbar: React.FC<Props> = ({
  query,
  isSearchFocused,
  isMobileToolbar,
  columnsAnchorRef,
  onSearchChange,
  onSearchFocus,
  onSearchBlur,
  onToggleColumns,
  onExport,
  isExporting = false,
  disableExport = false,
  disabled = false,
  columnsDropdown,
}) => {
  return (
    <View style={styles.headerActionsBar}>
      <View
        style={[
          styles.searchContainer,
          isMobileToolbar && styles.searchContainerMobile,
          isSearchFocused && styles.searchContainerFocused,
          disabled && styles.pagerBtnDisabled,
        ]}
      >
        <MaterialIcons name="search" size={18} color="#838383" />
        <TextInput
          style={[
            styles.searchInput,
            isMobileToolbar && styles.searchInputMobile,
            webInputFocusReset,
          ]}
          placeholder="Search locations..."
          placeholderTextColor="#9AA1AB"
          value={query}
          onChangeText={onSearchChange}
          onBlur={onSearchBlur}
          editable={!disabled}
          testID="asn-v2-search"
        />
      </View>

      <View
        style={styles.relativePositioning}
        ref={(node) => {
          columnsAnchorRef.current = node;
        }}
      >
        <TouchableOpacity
          onPress={onToggleColumns}
          disabled={disabled}
          testID="asn-v2-column-menu"
          style={[
            styles.columnMenuButton,
            isMobileToolbar && styles.columnMenuButtonMobile,
            disabled && styles.pagerBtnDisabled,
          ]}
        >
          <MaterialIcons name="tune" size={16} color="#005CBC" />
          <Text style={styles.menuButton}>Columns</Text>
        </TouchableOpacity>
        {columnsDropdown}
      </View>

      <TouchableOpacity
        style={[
          styles.exportButton,
          isMobileToolbar && styles.exportButtonMobile,
          (isExporting || disableExport || disabled) && styles.exportButtonDisabled,
        ]}
        testID="asn-gap-location-summary-v2-export"
        onPress={onExport}
        disabled={isExporting || disableExport || disabled}
        accessibilityRole="button"
        accessibilityLabel="Export to Excel"
      >
        {isExporting ? (
          <>
            <ActivityIndicator size="small" color="#005CBC" />
            <Text style={styles.exportButtonText}>Exporting...</Text>
          </>
        ) : (
          <>
            <MaterialIcons name="file-download" size={16} color="#005CBC" />
            <Text style={styles.exportButtonText}>Export to Excel</Text>
          </>
        )}
      </TouchableOpacity>
    </View>
  );
};

export default LocationSummaryToolbar;
